import React, { useRef, useState, useEffect } from "react";
import {
  Container,
  Typography,
  Grid,
  Paper,
  Card,
  CardContent,
  CardMedia,
  CardActionArea,
  Tooltip,
  BottomNavigation,
  BottomNavigationAction,
  TextField,
  Button,
  ThemeProvider,
  CssBaseline,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Menu,
  Slider,
  Fab,
} from "@mui/material";
import {
  SiHtml5,
  SiCss3,
  SiJavascript,
  SiReact,
  SiNodedotjs,
  SiExpress,
  SiPython,
  SiFlask,
  SiSqlalchemy,
  SiPostgresql,
  SiWindows,
} from "react-icons/si";
import HomeIcon from "@mui/icons-material/Home";
import InfoIcon from "@mui/icons-material/Info";
import BuildIcon from "@mui/icons-material/Build";
import WorkIcon from "@mui/icons-material/Work";
import ContactMailIcon from "@mui/icons-material/ContactMail";
import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import axios from "axios";

import lightTheme from "./Themes/lightTheme";
import darkTheme from "./Themes/darkTheme";
import dosTheme from "./Themes/dosTheme";
import win95Theme from "./Themes/win95Theme";
import { enableKeyboardNavigation } from "./utils/keyboardNavigation";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

function App() {
  const [value, setValue] = useState(0);
  const [theme, setTheme] = useState("light");
  const [anchorEl, setAnchorEl] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });
  const [formErrors, setFormErrors] = useState({});
  const homeRef = useRef(null);
  const aboutRef = useRef(null);
  const toolboxRef = useRef(null);
  const projectsRef = useRef(null);
  const contactRef = useRef(null);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const scrollToBottom = () => {
    window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
  };

  useEffect(() => {
    if (theme === "dos") {
      enableKeyboardNavigation();
    }
  }, [theme]);

  useEffect(() => {
    if (theme === "win95") {
      document.body.style.backgroundImage = "url(/win95-wallpaper.jpg)";
      document.body.style.backgroundSize = "cover";
      document.body.style.backgroundPosition = "center";
      document.body.style.backgroundRepeat = "no-repeat";
      document.body.style.minHeight = "100vh";
    } else {
      document.body.style.backgroundImage = "none";
    }
  }, [theme]);

  useEffect(() => {
    if (theme === "dark") {
      document.body.style.backgroundColor = "#2c2c2c"; // Dark gray for dark theme
      document.body.style.color = "#ffffff"; // White text for dark theme
    } else if (theme === "dos") {
      document.body.style.backgroundColor = "#000000"; // Black background for DOS theme
      document.body.style.color = "#00ff00"; // Green text for DOS theme
    } else {
      document.body.style.backgroundColor = "#f5f5f5"; // Light blue for light theme
      document.body.style.color = "#000000"; // Black text for light theme
    }
  }, [theme]);

  const handleNavClick = (ref) => {
    ref.current.scrollIntoView({ behavior: "smooth" });
  };

  const handleThemeChange = (event) => {
    setTheme(event.target.value);
  };

  const handleStartClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.name) errors.name = "Name is required";
    if (!formData.email) errors.email = "Email is required";
    if (!formData.message) errors.message = "Message is required";
    return errors;
  };

  const sendEmail = (e) => {
    e.preventDefault();

    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    axios
      .post("https://rosscentanni.com/send", formData)
      .then((response) => {
        console.log(response.data);
        alert("Email sent successfully!");
        setFormData({ name: "", email: "", message: "" });
        setFormErrors({});
      })
      .catch((error) => {
        console.error("There was an error sending the email!", error);
        alert("There was an error sending the email. Please try again later.");
      });
  };

  const currentTheme =
    theme === "light"
      ? lightTheme
      : theme === "dark"
      ? darkTheme
      : theme === "dos"
      ? dosTheme
      : win95Theme;

  const themeOptions = ["light", "dark", "dos", "win95"];

  return (
    <ThemeProvider theme={currentTheme}>
      <CssBaseline />
      <Container>
        <div
          style={{
            position: "fixed",
            top: 10,
            left: 10,
            width: "300px",
            padding: "20px",
            paddingRight: "40px",
          }}
        >
          <Typography variant="body1" gutterBottom>
            Select Mood
          </Typography>
          <Slider
            value={themeOptions.indexOf(theme)} // Map the current theme to the slider value
            onChange={(event, newValue) => setTheme(themeOptions[newValue])} // Update the theme based on the slider value
            step={1}
            marks
            min={0}
            max={themeOptions.length - 1}
            valueLabelDisplay="auto"
            valueLabelFormat={(value) => themeOptions[value]} // Display the theme name as the label
            sx={{
              color: "#5a479e", // Custom purple color for the slider
              "& .MuiSlider-thumb": {
                backgroundColor: "#5a479e", // Thumb color
              },
              "& .MuiSlider-track": {
                backgroundColor: "#5a479e", // Track color
              },
              "& .MuiSlider-rail": {
                backgroundColor: "#d1c4e9", // Lighter purple for the rail
              },
            }}
          />
        </div>
        <div style={{ position: "fixed", top: 10, right: 10 }}>
          <IconButton
            href="https://www.facebook.com/ross.centanni"
            target="_blank"
            color="inherit"
          >
            <FacebookIcon />
          </IconButton>
          <IconButton
            href="https://www.instagram.com/ross.centanni/"
            target="_blank"
            color="inherit"
          >
            <InstagramIcon />
          </IconButton>
          <IconButton
            href="https://www.linkedin.com/in/ross-centanni-2a666418/"
            target="_blank"
            color="inherit"
          >
            <LinkedInIcon />
          </IconButton>
        </div>
        {theme === "win95" && (
          <div
            style={{
              position: "fixed",
              bottom: 0,
              left: 0,
              width: "100%",
              height: "40px",
              backgroundColor: "#C0C0C0",
              borderTop: "2px solid #808080",
              boxShadow: "inset -1px -1px #FFFFFF, inset 1px 1px #808080",
              display: "flex",
              alignItems: "center",
            }}
          >
            <Button
              variant="contained"
              onClick={handleStartClick}
              style={{
                color: "#000000",
                backgroundColor: "#C0C0C0",
                border: "1px solid #808080",
                boxShadow: "inset -1px -1px #FFFFFF, inset 1px 1px #808080",
                textTransform: "none",
                marginLeft: "10px",
              }}
            >
              Start
            </Button>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
            >
              <MenuItem
                onClick={() => {
                  handleNavClick(homeRef);
                  handleMenuClose();
                }}
              >
                Home
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleNavClick(aboutRef);
                  handleMenuClose();
                }}
              >
                About
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleNavClick(toolboxRef);
                  handleMenuClose();
                }}
              >
                Toolbox
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleNavClick(projectsRef);
                  handleMenuClose();
                }}
              >
                Projects
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleNavClick(contactRef);
                  handleMenuClose();
                }}
              >
                Contact
              </MenuItem>
            </Menu>
          </div>
        )}
        <div ref={homeRef}></div>
        <Grid container spacing={3} style={{ marginTop: 20 }}>
          <Grid item xs={12}>
            <Paper style={{ padding: 20 }}>
              <Typography
                variant="h1"
                // style={{
                //   fontFamily: "'Montserrat', sans-serif", // Suggested complementary font
                //   fontWeight: 700,
                // }}
                gutterBottom
              >
                Hi! I'm Ross,
              </Typography>
            </Paper>
            <Paper style={{ padding: 10 }}>
              <Typography variant="h4" gutterBottom>
                What can I build for you today?
              </Typography>
              {/* <Typography variant="body1">
                Hi, I'm a pharmacist turned software developer with a passion for building applications. I have experience in various technologies including React, Node.js, Python, Flask and more. I'm always looking for new projects to work on, so feel free to reach out to me if you have an idea you'd like to bring to life.
              </Typography> */}
            </Paper>
          </Grid>
          <div ref={aboutRef}></div>
          <Grid item xs={12} sm={6}>
            <Paper style={{ padding: 20 }}>
              <Typography variant="h5" gutterBottom>
                About Me
              </Typography>
              {/* <CardMedia
                component="img"
                alt="Profile Photo"
                height="550"
                width="200"
                image={theme === 'dos' ? "/portrait1-dos.jpg" : "/portrait1.jpg"} // Replace with your profile photo URL
                title="Profile Photo"
                style={{ marginBottom: 20, borderRadius: '50%' }}
              /> */}
              <Typography variant="body1">
                I'm a pharmacist turned software developer with a passion for
                building applications. I have experience in various technologies
                including React, Node.js, Python, Flask and more. I'm always
                looking for new projects to work on, so feel free to reach out
                to me if you have an idea you'd like to bring to life.
              </Typography>
            </Paper>
          </Grid>
          <div ref={toolboxRef}></div>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h5" gutterBottom>
                  My Toolbox
                </Typography>
                <Grid container spacing={2} justifyContent="center">
                  <Grid item>
                    <Tooltip title="HTML5: Markup language for structuring web content">
                      <SiHtml5 size={40} />
                    </Tooltip>
                  </Grid>
                  <Grid item>
                    <Tooltip title="CSS3: Style sheet language for designing web pages">
                      <SiCss3 size={40} />
                    </Tooltip>
                  </Grid>
                  <Grid item>
                    <Tooltip title="JavaScript: Programming language for web development">
                      <SiJavascript size={40} />
                    </Tooltip>
                  </Grid>
                  <Grid item>
                    <Tooltip title="React: JavaScript library for building user interfaces">
                      <SiReact size={40} />
                    </Tooltip>
                  </Grid>
                  <Grid item>
                    <Tooltip title="Node.js: JavaScript runtime for server-side programming">
                      <SiNodedotjs size={40} />
                    </Tooltip>
                  </Grid>
                  <Grid item>
                    <Tooltip title="Express: Web application framework for Node.js">
                      <SiExpress size={40} />
                    </Tooltip>
                  </Grid>
                  <Grid item>
                    <Tooltip title="Python: High-level programming language">
                      <SiPython size={40} />
                    </Tooltip>
                  </Grid>
                  <Grid item>
                    <Tooltip title="Flask: Web framework for Python">
                      <SiFlask size={40} />
                    </Tooltip>
                  </Grid>
                  <Grid item>
                    <Tooltip title="SQLAlchemy: SQL toolkit and ORM for Python">
                      <SiSqlalchemy size={40} />
                    </Tooltip>
                  </Grid>
                  <Grid item>
                    <Tooltip title="PostgreSQL: Relational database management system">
                      <SiPostgresql size={40} />
                    </Tooltip>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
          <div ref={projectsRef}></div>
          <Grid item xs={12}>
            <Typography variant="h5" gutterBottom>
              Projects
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={4}>
                <Card>
                  <CardActionArea>
                    <CardMedia
                      component="img"
                      height="200"
                      image={
                        theme === "dos"
                          ? "/sugarcanelogodos-2.png"
                          : "/sugarcanelogo.png"
                      }
                      alt="Project 1"
                    />
                    <CardContent>
                      <Typography gutterBottom variant="h5" component="div">
                        Sample Restaurant App
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        A React/Express based demo app for a fictitious
                        restaurant. Features include a landing page, menu
                        section with various categories, a reservation form,
                        contact page, and an admin section. The admin section
                        currently features reservation handling and menu
                        management; along with the capacity for much more.
                        Technologies employed: React, Express, Node.js,
                        PostgreSQL, Material-UI, CSS, Adobe Illustrator.
                      </Typography>
                      <Button
                        variant="contained"
                        href="https://www.rosscentanni.com/projects/restaurant/"
                        target="_blank"
                        style={{
                          marginTop: "10px",
                          backgroundColor: "#5a479e", // Custom purple color
                          color: "#ffffff", // White text for contrast
                        }}
                      >
                        Check it out
                      </Button>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Card>
                  <CardActionArea>
                    <CardMedia
                      component="img"
                      height="140"
                      image={
                        theme === "dos"
                          ? "/squawk_logo_dos.png"
                          : "/squawk_logo2.png"
                      }
                      alt="Project 2"
                    />
                    <CardContent>
                      <Typography gutterBottom variant="h5" component="div">
                        Squawk! A social media app
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Squawk, is a Flask based social media platform inspired
                        by Twitter, designed for users to share short messages
                        ("squawks") with additional features like links and
                        images. It provides a personalized timeline, user
                        interaction capabilities, and administrative tools for
                        managing content. Squawk! started as a course project
                        that I improved and rebranded. This is a fully
                        functional site, so go to squawkproject.com, sign up and
                        follow me @rosscentanni. Technologies used: HTML, CSS,
                        Python, Flask, PostgreSQL, Bootstap
                      </Typography>
                      <Button
                        variant="contained"
                        href="https://squawkproject.com"
                        target="_blank"
                        style={{
                          marginTop: "10px",
                          backgroundColor: "#5a479e", // Custom purple color
                          color: "#ffffff", // White text for contrast
                        }}
                      >
                        Check it out
                      </Button>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Card>
                  <CardActionArea>
                    <CardMedia
                      component="img"
                      height="140"
                      image={
                        theme === "dos"
                          ? "/online-store-demo-logo-dos.png"
                          : "/online-store-demo-logo.png"
                      }
                      alt="Project 3"
                    />
                    <CardContent>
                      <Typography gutterBottom variant="h5" component="div">
                        Online Store Demo
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        This is a demo version of an e-commerce project. It is
                        fully featured, including product categories, search,
                        customer feedback, currency conversion, customer
                        invoices and tracking via email, full payment
                        functionality via Stripe, and shipping functionality.
                        The Admin Dashboard includes product maintainence, and
                        order handling/tracking. Technologies used: Python, Flask,
                        PostgreSQL, Stripe, HTML, CSS, Bootstap
                      </Typography>
                      <Button
                        variant="contained"
                        href="https://rconlinestoreproject.dev"
                        target="_blank"
                        style={{
                          marginTop: "10px",
                          backgroundColor: "#5a479e", // Custom purple color
                          color: "#ffffff", // White text for contrast
                        }}
                      >
                        Check it out
                      </Button>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            </Grid>
          </Grid>
          <div ref={contactRef}></div>
          <Grid container spacing={3} style={{ marginTop: 20 }}>
            {/* Profile Photo */}
            <Grid item xs={12} sm={4}>
              <CardMedia
                component="img"
                alt="Profile Photo"
                height="375"
                image={
                  theme === "dos" ? "/portrait1-dos.jpg" : "/portrait1.jpg"
                } // Replace with your profile photo URL
                title="Profile Photo"
                style={{
                  borderRadius: "100%",
                  margin: "0 auto",
                  display: "block",
                }}
              />
            </Grid>

            {/* Contact Form */}
            <Grid item xs={12} sm={8}>
              <Typography variant="h5" gutterBottom>
                Contact Me
              </Typography>
              <Paper style={{ padding: 20 }}>
                <form noValidate autoComplete="off" onSubmit={sendEmail}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        required
                        id="name"
                        label="Name"
                        name="name"
                        fullWidth
                        value={formData.name}
                        onChange={handleInputChange}
                        error={!!formErrors.name}
                        helperText={formErrors.name}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        required
                        id="email"
                        label="Email"
                        name="email"
                        type="email"
                        fullWidth
                        value={formData.email}
                        onChange={handleInputChange}
                        error={!!formErrors.email}
                        helperText={formErrors.email}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        required
                        id="message"
                        label="Message"
                        name="message"
                        multiline
                        rows={4}
                        fullWidth
                        value={formData.message}
                        onChange={handleInputChange}
                        error={!!formErrors.message}
                        helperText={formErrors.message}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        variant="contained"
                        type="submit"
                        sx={{
                          backgroundColor: "#5a479e", // Custom purple color
                          color: "#ffffff", // White text for contrast
                          "&:hover": {
                            backgroundColor: "#4a3a7e", // Slightly darker purple on hover
                          },
                        }}
                      >
                        Send
                      </Button>
                    </Grid>
                  </Grid>
                </form>
              </Paper>
            </Grid>
          </Grid>
        </Grid>
        <footer style={{ textAlign: "center", marginTop: 40 }}>
          <Typography variant="h6" gutterBottom>
            Ross Centanni
          </Typography>
        </footer>
        {theme !== "win95" && theme !== "dos" && (
          <BottomNavigation
            value={value}
            onChange={(event, newValue) => {
              setValue(newValue);
              switch (newValue) {
                case 0:
                  handleNavClick(homeRef);
                  break;
                case 1:
                  handleNavClick(aboutRef);
                  break;
                case 2:
                  handleNavClick(toolboxRef);
                  break;
                case 3:
                  handleNavClick(projectsRef);
                  break;
                case 4:
                  handleNavClick(contactRef);
                  break;
                default:
                  break;
              }
            }}
            showLabels
            style={{
              position: "fixed",
              bottom: 20,
              left: "50%",
              transform: "translateX(-50%)",
              borderRadius: 50,
              width: "80%",
              backgroundColor: "#fff",
              boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.1)",
            }}
          >
            <BottomNavigationAction
              label="Home"
              icon={
                <HomeIcon
                  sx={{ color: theme === "dark" ? "black" : "inherit" }}
                />
              }
              sx={{ color: theme === "dark" ? "black" : "inherit" }}
            />
            <BottomNavigationAction
              label="About"
              icon={
                <InfoIcon
                  sx={{ color: theme === "dark" ? "black" : "inherit" }}
                />
              }
              sx={{ color: theme === "dark" ? "black" : "inherit" }}
            />
            <BottomNavigationAction
              label="Toolbox"
              icon={
                <BuildIcon
                  sx={{ color: theme === "dark" ? "black" : "inherit" }}
                />
              }
              sx={{ color: theme === "dark" ? "black" : "inherit" }}
            />
            <BottomNavigationAction
              label="Projects"
              icon={
                <WorkIcon
                  sx={{ color: theme === "dark" ? "black" : "inherit" }}
                />
              }
              sx={{ color: theme === "dark" ? "black" : "inherit" }}
            />
            <BottomNavigationAction
              label="Contact"
              icon={
                <ContactMailIcon
                  sx={{ color: theme === "dark" ? "black" : "inherit" }}
                />
              }
              sx={{ color: theme === "dark" ? "black" : "inherit" }}
            />
          </BottomNavigation>
        )}
      </Container>
    </ThemeProvider>
  );
}

export default App;
